// RideAndQRButtons — iter104 two-row prominent layout.
//
// Row 1 (full-width, prominent):
//   ┌──────────────────────────────────────────────────┐
//   │  🚗  Request a Ride                              │  ← gold gradient
//   └──────────────────────────────────────────────────┘     52px tall, shadow
//
// Row 2 (side-by-side, no phone) — or 3 items when phone is present:
//   ┌─────────────────────┐ ┌─────────────────────┐ ┌────┐
//   │ ▥ View Directions   │ │ ☎  Contact          │ │ ☎  │
//   └─────────────────────┘ └─────────────────────┘ └────┘
//        outline               WhatsApp green         circle, only if phone
//
// • Request a Ride → opens the global WhatsAppActionSheet (ride flow).
// • View Directions → opens DriverQRModal with this location's coords.
//   Hidden when destination has no lat/lng.
// • Phone circle → opens the global WhatsAppActionSheet (business flow).
//   Hidden when destination has neither `phone` nor `whatsapp`.
//
// Props mirror the previous component for drop-in compatibility:
//   - destination: { name, address?, lat?, lng?, category?, neighborhood?,
//                    phone?, whatsapp?, id? }
//   - testidPrefix: string used for both buttons' data-testid prefixes.
//   - rideLabelKey: i18n key for the ride button label (defaults to
//     "ride_request_driver"). Friend-profile hotel uses
//     "ride_request_driver_to_hotel".
import { useState } from "react";
import { Car, QrCode, Phone } from "lucide-react";
import { useI18n } from "../lib/i18n";
import DriverQRModal from "./DriverQRModal";
import { sendRideRequest, openContactPopup } from "../lib/sendRideRequest";

export default function RideAndQRButtons({
  destination,
  testidPrefix,
  rideLabelKey = "ride_request_driver",
  className = "",
}) {
  const { t } = useI18n();
  const [qrOpen, setQrOpen] = useState(false);
  if (!destination || !destination.name) return null;
  const hasCoords = destination.lat != null && destination.lng != null;
  const hasPhone = !!(destination.phone || destination.whatsapp);

  return (
    <div className={className} data-testid={`${testidPrefix}-buttons`}>
      {/* Row 1 — Primary Request a Ride */}
      <button
        type="button"
        data-testid={`${testidPrefix}-request-driver`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          sendRideRequest(destination, { source: testidPrefix });
        }}
        className="w-full inline-flex items-center justify-center gap-2 transition-opacity hover:opacity-95"
        style={{
          height: 52,
          background: "linear-gradient(180deg, #F5CC55, #E8B84B)",
          color: "#0B0B0B",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 16,
          fontWeight: 700,
          letterSpacing: "0.02em",
          borderRadius: 12,
          border: "none",
          padding: "0 14px",
          boxShadow: "0 4px 16px rgba(232,184,75,0.35)",
        }}
      >
        <Car style={{ width: 18, height: 18 }} strokeWidth={2} />
        <span>{t(rideLabelKey)}</span>
      </button>

      {/* Row 2 — Directions + optional Phone */}
      {(hasCoords || hasPhone) && (
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          {hasCoords && (
            <button
              type="button"
              data-testid={`${testidPrefix}-get-qr`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQrOpen(true); }}
              className="inline-flex items-center justify-center gap-1.5 transition-colors hover:bg-white/[0.04]"
              style={{
                flex: 1,
                height: 44,
                background: "transparent",
                color: "#E8B84B",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: "0.02em",
                borderRadius: 10,
                border: "1.5px solid rgba(232,184,75,0.55)",
                padding: "0 10px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <QrCode className="w-4 h-4 shrink-0" strokeWidth={2} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{t("ride_view_directions")}</span>
            </button>
          )}

          {hasPhone && (
            <button
              type="button"
              data-testid={`${testidPrefix}-phone`}
              aria-label={t("ride_phone_contact")}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openContactPopup(destination, { source: `${testidPrefix}-phone` });
              }}
              className="grid place-items-center transition-colors hover:bg-white/[0.04]"
              style={{
                width: 44,
                height: 44,
                flex: "0 0 44px",
                borderRadius: 999,
                background: "transparent",
                color: "#E8B84B",
                border: "1.5px solid rgba(232,184,75,0.55)",
                padding: 0,
              }}
            >
              <Phone className="w-4 h-4" strokeWidth={2} />
            </button>
          )}
        </div>
      )}

      {hasCoords && (
        <div
          className="text-center mt-1.5"
          style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#9A9490" }}
        >
          {t("ride_qr_works_offline")}
        </div>
      )}

      <DriverQRModal
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        business={destination}
        testidPrefix={`${testidPrefix}-qr`}
      />
    </div>
  );
}
